import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useUserContext } from '../../contexts/UserContext';
import { getApolloErrorMessages } from '../../lib/errors';
import Card from '../Card';
import { FormStatus } from '../FormStatus';
import { WithPortal } from '../PortalLayout';
import Spinner from '../Spinner';
import TextLink from '../TextLink';
import { verifyEmailOnce } from './mutations';

type Result = { verified: true; error?: never } | { verified?: never; error: unknown };

function Content() {
  const { token } = useParams<{ token: string }>();
  const { isLoggedIn, userLoading, refetch } = useUserContext();
  const [result, setResult] = useState<Result>();

  useEffect(() => {
    let isMounted = true;
    verifyEmailOnce(token).then(
      () => isMounted && setResult({ verified: true }),
      (error: unknown) => isMounted && setResult({ error }),
    );
    return () => {
      isMounted = false;
    };
  }, [token]);

  // the page load fetches the signed-in user while the email is being verified, and Apollo hands a refetch
  // the result of the same query still in flight: refetch only once that first load is done, or the user
  // would keep the unverified email until the next reload
  const hasRefetchedUser = useRef(false);
  useEffect(() => {
    if (!result?.verified || !isLoggedIn || userLoading || hasRefetchedUser.current) return;
    hasRefetchedUser.current = true;
    refetch?.();
  }, [result, isLoggedIn, userLoading, refetch]);

  const { error } = result ?? {};
  if (error) {
    return (
      <>
        <FormStatus type="error">
          {getApolloErrorMessages(error) || (error instanceof Error ? error.message : 'Something went wrong')}
        </FormStatus>
        <p className="mt-4">
          {isLoggedIn ? (
            <>
              You can send a new link from your <TextLink to="/settings/profile">settings</TextLink>.
            </>
          ) : (
            <>
              <TextLink to="/login">Sign in</TextLink> to send a new link from your settings.
            </>
          )}
        </p>
      </>
    );
  }

  if (!result?.verified) {
    return (
      <div className="flex flex-col items-center pb-8">
        <Spinner className="mt-12" />
      </div>
    );
  }

  return (
    <p>
      {isLoggedIn ? (
        <>
          You can now turn on two-factor authentication in your{' '}
          <TextLink to="/settings/profile">settings</TextLink>.
        </>
      ) : (
        <>
          <TextLink to="/login">Log in</TextLink> to turn on two-factor authentication.
        </>
      )}
    </p>
  );
}

function VerifyEmail() {
  return (
    <div className="mt-12 w-full max-w-lg">
      <Card padding="p-6">
        <Content />
      </Card>
    </div>
  );
}

export default React.memo(WithPortal(VerifyEmail, { className: 'pt-16 flex flex-col items-center' }));
